import { EventEmitter } from 'events';
import CryptoJS from 'crypto-js';
import { message } from 'antd';
import TypedEmitter from 'typed-emitter';
import axios, { AxiosInstance, Method } from 'axios';
import { Socket } from 'socket.io-client';
import { createSocket } from '../../libs/socket';
import { logger } from '../../libs/utils/logger';
import { trackOpenReplayEvent, trackOpenReplayIssue } from '../../libs/utils/openreplay';
import {
	APP_CONFIG, BadActivitiesTypeString, CheatingType, QuestionType, TestCaseStatus, isElectronApp,
	localStorageKeyForEncryptino,
	localStorageKeyForVmDetection,
} from '../../config';
import { checkIfPayloadNeedDecription, currentPlatform, decodePayload } from '../../libs/utils';
import { checkFirewallStatus } from '../../libs/utils/fire-wall';

interface CQQuizClientOptions {
	quizServerURL: string
	quizAdminServerURL: string
	socketServerURL: string
	staticStorageApiURL: string
	notificationServiceURL: string
}

interface AuthSucceedResponse {
	msg: string
	id: string // session id
}

interface LogoutUserConfig {
	redirect?: string,
	reload?: boolean,
}

interface ClientEvents {
	'auth': (data: SessionData) => Promise<void>

	'socket-connected': () => void
	'socket-disconnected': () => void
	'socket-auth': () => void
	'compile-result': (data: any) => void
	'section-update': () => void
	'force-submit': (type?: number) => void
	'logout-user': (data: LogoutUserConfig) => void
	'multiple-connection-detected': (status: string) => void

	'quiz-started': (data: any) => Promise<void>
	'quiz-data-received': (data: QuizData) => Promise<void>
	'quiz-dashboard-data-received': (data: any) => Promise<void>
	'quiz-segment-submitted': (segmentIndex: number) => Promise<void>
	'quiz-submitted': () => Promise<void>
	'concurrent_user_exeded': () => void
	'attempt-question-data-received': (data: AttemptQuestionData) => Promise<void>
	'question-submitted': (questionId: string) => Promise<void>
	'quiz-updated': (updatedAt: string) => Promise<void>
	'quiz-remaining-time-updated': (time: number) => Promise<void>
	'quiz-suspicious-updated': (data: any) => Promise<void>
	'server-time': (data: any) => Promise<void>
	'change-room': (roomName?: string) => void,
	'app-config-change': (data: { stopAiProctoring?: boolean, stopRecording?: boolean }) => void;
	'identity_check_code': (data: { code: string }) => void;
}

interface SocketEmitEvents {
	notification: (data: any) => void
	compile: (compileRequest: CodeCompilationRequest, ack: (error?: unknown) => void) => void
	update_jitsi_credentials: (data: NonNullable<JitsiCredentials>) => void
	send_button_submission_logs: (data: SubmissionPayload) => void
	recording_upload_event: (data: any) => void
	removed_user_from_room: (data: { roomName: string }) => void
	quiz_remaining_time: (data: any) => void
	heart_beat_complete: (data: any) => void;
}

interface SocketListenEvents {
	liveness_check: (message: string, ack: (response: 'yes') => void) => void;
	auth_succeed: (authSucceedResponse: AuthSucceedResponse) => void
	compile: (compileResult: any) => void
	session_expired: (data: any) => void
	logout_user: (data: any) => void
	concurrent_user_exeded: (data: any) => void
	force_submit: () => void
	section_update: () => void
	change_room: (roomName?: string) => void
	app_config_change: () => void
	quiz_remaining_time: (payload: QuizRemainingTimeResponse) => void
	check_firewall_status: (ack: (data: FirewallCheckResponse) => void) => void;
	heart_beat: (data: any) => void;
	identity_check_code: (payload: { code: string }) => void;
	multiple_connection_status: (status: string) => void;
}

interface mainApiRequestParams {
	api?: '_mainAPI' | '_quizAdminAPI';
	method: Method;
	url: string, params?: any;
	payload?: any;
	config?: any;
	headers?: any;
}

interface mainApiRequestConfig {
	allowWithoutLogin: boolean,
	retryOnEncFail?: boolean,
	skipEnc?: boolean,
	encV2?: boolean,
	getRowAxiosError?: boolean,
}

function getJsonPayloadSize(payload: unknown): number | null {
	try {
		return JSON.stringify(payload).length;
	} catch (error) {
		return null;
	}
}

export class CQQuizClient extends (EventEmitter as new () => TypedEmitter<ClientEvents>) {
	private _options: CQQuizClientOptions;

	private _socket: Socket<SocketListenEvents, SocketEmitEvents>;

	private _mainAPI: AxiosInstance;

	private _quizAdminAPI: AxiosInstance;

	private _notificationServiceAPI: AxiosInstance;

	private _staticStorageAPI: AxiosInstance;

	private _sessionData?: SessionData;

	private quizId: string | null;

	private _disposables: (() => void)[] = [];

	private _quizServerUrl: string;

	// variation of each loaded question; runs and submissions are graded against it
	private _variationIdByQuestion = new Map<string, string>();

	constructor(options: CQQuizClientOptions) {
		super();
		const {
			quizServerURL, quizAdminServerURL,
			staticStorageApiURL, socketServerURL, notificationServiceURL,
		} = options;

		this.quizId = null;

		const socket = createSocket({
			path: '/socket.io',
			endpoint: socketServerURL,
		});

		const mainAPI = axios.create({
			baseURL: quizServerURL,
			responseType: 'json',
			withCredentials: true,
		});

		const quizAdminAPI = axios.create({
			baseURL: quizAdminServerURL,
			responseType: 'json',
			withCredentials: true,
		});

		const notificationServiceAPI = axios.create({
			baseURL: notificationServiceURL,
			responseType: 'json',
			withCredentials: true,
		});

		const staticStorageAPI = axios.create({
			baseURL: staticStorageApiURL,
			responseType: 'json',
			withCredentials: true,
		});

		this._socket = socket;
		this._mainAPI = mainAPI;
		this._quizAdminAPI = quizAdminAPI;
		this._notificationServiceAPI = notificationServiceAPI;
		this._staticStorageAPI = staticStorageAPI;
		this._options = options;
		this._quizServerUrl = quizServerURL;

		this._attachClientListeners();
		this._attachSocketListeners();

		this._disposables.push(
			this._detachClientListeners,
			this._detachSocketListeners,
			this.removeAllListeners.bind(this),
		);
	}

	private _sendNotification = async (
		payload: Record<string, unknown> & { data?: Record<string, unknown> },
		headers?: Record<string, unknown>,
		options?: {
			sendToNotificationsServer?: boolean;
			handleSendForNonNotificationsServer?(
				client: CQQuizClient,
				payload: Record<string, unknown>
			): Promise<void>;
		},
	) => {
		if (this._sessionData === undefined) {
			return;
		}

		const completePayload: Record<string, unknown> & { data: Record<string, unknown> } = {
			sid: this._sessionData.sessionId,
			userId: this._sessionData.userId,
			...payload,
			data: payload.data ?? {},
		};

		// Need to get the test-wise config ["sendToNotificationService" or something]
		const sendToNotificationsServer = options?.sendToNotificationsServer ?? true;

		if (!sendToNotificationsServer) {
			if (options?.handleSendForNonNotificationsServer) {
				await options.handleSendForNonNotificationsServer(this, completePayload);
				return;
			}
			if (this.connected) {
				this._socket.emit('notification', completePayload);
			}
			return;
		}
		if (completePayload.cheatingType === CheatingType.fullScreen) {
			completePayload.data.fullScreenType = completePayload.data.tabSwitchType;
			delete completePayload.data.tabSwitchType;
		}

		const finalPayload: Record<string, unknown> = completePayload;
		const cHeaders: Record<string, unknown> = headers ?? {};

		await this._notificationServiceAPI.post(
			'/notifications',
			finalPayload,
			{
				headers: cHeaders,
			},
		);
	};

	private _onAuth = async (sessionData: SessionData): Promise<void> => {
		this._sessionData = {
			...this._sessionData,
			...sessionData,
		};
	};

	private _attachClientListeners = (): void => {
		this.on('auth', this._onAuth);
	};

	private _detachClientListeners = (): void => {
		this.off('auth', this._onAuth);
	};

	private _submitQuiz = (...args): void => {
		const callback = args.pop();
		const type = args.pop();
		if (callback && typeof callback === 'function') {
			callback({
				status: 'ok',
			});
		}
		this.emit('force-submit', type);
	};

	private _onSocketConnect = (): void => {
		this.emit('socket-connected');
	};

	private _onSocketDisconnect = (): void => {
		this.emit('socket-disconnected');
	};

	private _onLivenessCheck: SocketListenEvents['liveness_check'] = (_message, ack) => {
		ack('yes');
	};

	private _onSectionUpdate = (): void => {
		this.emit('section-update');
	};

	private _onConcurrentUserExeded = (): void => {
		localStorage.setItem('socketLogout', JSON.stringify({
			date: Date.now(),
			msg: 'Concurrent User Exeeded.',
		}));
		this.emit('socket-disconnected');
	};

	private _onSocketAuthSucceed = (payload: AuthSucceedResponse): void => {
		const { id: sessionId } = payload || {};
		if (!sessionId) throw new Error('auth_succeed: sessionId not found');

		const { _sessionData } = this;
		if (!_sessionData) throw new Error('session data does not exist');

		const sessionData = {
			..._sessionData,
			sessionId,
		};

		this.emit('auth', sessionData);
		this.emit('socket-auth');
	};

	private _onMultipleConnectionStatus = (): void => {
		this.emit('multiple-connection-detected', 'Multiple connections were detected for this session. Please contact your test administrator.');
	};

	private _onSessionExpired = (data: any): void => {
		if (data && data.msg) {
			localStorage.setItem('socketLogout', JSON.stringify({
				date: Date.now(),
				msg: data.msg,
			}));
		}
		this.emit('logout-user', { reload: true });
	};

	private _onlogoutUser = (): void => {
		this.emit('logout-user', {
			redirect: `${APP_CONFIG.QuizServerURL}/logout`,
		});
	};

	private _onCompileResult = (data: unknown): void => {
		this.emit('compile-result', data);
	};

	private _checkFireWallStatus = async (ack: (data: FirewallCheckResponse) => void) => {
		try {
			const fireWallDetails = await checkFirewallStatus();
			if (ack) {
				ack(fireWallDetails);
			}
		} catch (error) {
			console.error(error);
		}
	};

	private _changeRoom = (data?: string): void => {
		this.emit('change-room', data);
	};

	private _onIdentityCheckCode = (payload: { code: string }): void => {
		this.emit('identity_check_code', payload);
	};

	private _appConfigChange = (): void => {
		this.appConfigChage();
	};

	private async _getRemainingTime(payload: Record<string, any>) {
		const isPayloadEncrypted = checkIfPayloadNeedDecription(payload);
		if (isPayloadEncrypted) {
			// eslint-disable-next-line no-param-reassign
			payload = await decodePayload(payload);
		}
		const data = payload as QuizRemainingTimeResponse;
		if (data.error) {
			throw new Error(data.error);
		}
		const time: number = +data.remainingTime + +data.extraTime;
		if (data.quizUpdatedAt) {
			this.emit('quiz-updated', data.quizUpdatedAt);
		}
		this.emit('quiz-remaining-time-updated', time);
		this.emit('quiz-suspicious-updated', data);
	}

	private _attachSocketListeners = (): void => {
		const { _socket } = this;

		_socket.on('connect', this._onSocketConnect);
		_socket.on('disconnect', this._onSocketDisconnect);

		_socket.on('liveness_check', this._onLivenessCheck);

		_socket.on('auth_succeed', this._onSocketAuthSucceed);
		_socket.on('session_expired', this._onSessionExpired);
		_socket.on('logout_user', this._onlogoutUser);

		_socket.on('compile', this._onCompileResult);
		_socket.on('section_update', this._onSectionUpdate);
		_socket.on('concurrent_user_exeded', this._onConcurrentUserExeded);
		_socket.on('force_submit', this._submitQuiz);
		_socket.on('change_room', this._changeRoom);
		_socket.on('app_config_change', this._appConfigChange);
		_socket.on('identity_check_code', this._onIdentityCheckCode);
		_socket.on('quiz_remaining_time', this._getRemainingTime);
		_socket.on('check_firewall_status', this._checkFireWallStatus);
		_socket.on('heart_beat', this.remoteAction);
		_socket.on('multiple_connection_status', this._onMultipleConnectionStatus);
	};

	private _detachSocketListeners = (): void => {
		const { _socket } = this;

		_socket.off('connect', this._onSocketConnect);
		_socket.off('disconnect', this._onSocketDisconnect);

		_socket.off('auth_succeed', this._onSocketAuthSucceed);
		_socket.off('session_expired', this._onSessionExpired);
		_socket.off('logout_user', this._onlogoutUser);

		_socket.off('compile', this._onCompileResult);
		_socket.off('quiz_remaining_time', this._getRemainingTime);
		_socket.off('identity_check_code', this._onIdentityCheckCode);
		_socket.off('multiple_connection_status', this._onMultipleConnectionStatus);
	};

	get connected(): boolean {
		return this._socket.connected;
	}

	connect = async (): Promise<void> => {
		const { _socket, _sessionData } = this;

		if (!_sessionData) {
			throw new Error('connection cannot be created before user auth.');
		}

		try {
			const deviceId = await (window as any).api?._invoke('_get-device-secret');
			if (deviceId) {
				(_socket.io as any).opts.query = { deviceId };
			}
		} catch { /* non-Electron environment */ }

		return new Promise((resolve, reject) => {
			const timer = setTimeout(() => {
				reject(new Error('Something went wrong. Please check your internet connection.'));
			}, 15000);

			let handleSocketConnectionFailed = () => { };

			const handleSocketAuthConnection = () => {
				_socket.off('connect_error', handleSocketAuthConnection);
				this.off('socket-auth', handleSocketConnectionFailed);
				clearTimeout(timer);
				resolve();
			};

			handleSocketConnectionFailed = () => {
				_socket.off('connect_error', handleSocketConnectionFailed);
				this.off('socket-auth', handleSocketAuthConnection);
				reject(new Error('Uable to create socket connection.'));
			};

			this.once('socket-auth', handleSocketAuthConnection);
			_socket.once('connect_error', handleSocketConnectionFailed);
			_socket.connect();
		});
	};

	destroy = async (): Promise<void> => {
		const { _disposables } = this;

		await Promise.all(_disposables.map((el) => el()));
		this._disposables = [];
	};

	mainApiRequestSend = async (
		{
			method, url, params, payload, config, headers, api = '_mainAPI',
		}: mainApiRequestParams,
		requestConfig?: mainApiRequestConfig,
	): Promise<any> => {
		let isLogout = 0;
		try {
			if (payload && isElectronApp && 'api' in window && !(payload instanceof FormData) && !requestConfig?.skipEnc) {
				if (!headers) {
					// eslint-disable-next-line no-param-reassign
					headers = {};
				}
				if (requestConfig?.encV2) {
					const encPayload = await (window.api as any)._invoke('_encrypt-payload-v2', payload);
					if (encPayload) {
						// eslint-disable-next-line no-param-reassign
						payload = encPayload?.payload ?? {
							...payload,
							invalidLoginEncFailed: true,
						};
						Object.keys(encPayload?.headers ?? {}).forEach((headerKey) => {
							// eslint-disable-next-line no-param-reassign
							headers[headerKey] = encPayload.headers[headerKey];
						});
					} else {
						const encPayload2 = await (window.api as any)._invoke('_encrypt-payload', payload);
						// eslint-disable-next-line no-param-reassign
						payload = encPayload2?.payload ?? {
							...payload,
							invalidLoginEncFailed: true,
						};
						Object.keys(encPayload2?.headers ?? {}).forEach((headerKey) => {
							// eslint-disable-next-line no-param-reassign
							headers[headerKey] = encPayload2.headers[headerKey];
						});
					}
				} else {
					const encPayload = await (window.api as any)._invoke('_encrypt-payload', payload);
					// eslint-disable-next-line no-param-reassign
					payload = encPayload?.payload ?? {
						...payload,
						invalidLoginEncFailed: true,
					};
					Object.keys(encPayload?.headers ?? {}).forEach((headerKey) => {
						// eslint-disable-next-line no-param-reassign
						headers[headerKey] = encPayload.headers[headerKey];
					});
				}
			}
			const apiMethod = this[api];
			const response = await apiMethod({
				method,
				url,
				data: payload,
				params,
				...config,
				headers,
			});

			if (response.data.error && response.status === 401) {
				isLogout = 101;
				throw new Error(response.data.error);
			}
			if (response.status === 499) {
				isLogout = 102;
				localStorage.setItem(localStorageKeyForVmDetection, JSON.stringify(Date.now()));
			}
			if (checkIfPayloadNeedDecription(response.data)) {
				const decodedData = await decodePayload(response.data);
				response.data = decodedData;
			}

			return response;
		} catch (error: any) {
			logger.log(error?.response);
			if (checkIfPayloadNeedDecription(error?.response?.data)) {
				const decodedData = await decodePayload(error.response.data);
				error.response.data = decodedData;
			}
			if (axios.isAxiosError(error) && requestConfig?.getRowAxiosError) {
				throw error;
			}
			if (error?.response?.data?.error) {
				if (error?.response.status === 401) {
					isLogout = 103;
				}
				if (error?.response.status === 207) {
					isLogout = 104;
				}
				if (error?.response.status === 499) {
					isLogout = 105;
					localStorage.setItem(localStorageKeyForVmDetection, JSON.stringify(Date.now()));
				}
				throw new Error(error.response.data.error);
			} else if (axios.isAxiosError(error)) {
				if (error?.response?.status === 422) {
					if (requestConfig?.retryOnEncFail) {
						message.error('Oh, something went wrong, please wait while we retry.');
						this._appConfigChange();
						await new Promise((res, rej) => setTimeout(res, 3_000));
						return await this.mainApiRequestSend({
							method,
							url,
							params,
							headers,
							payload,
							config,
						}, {
							allowWithoutLogin: requestConfig.allowWithoutLogin,
							retryOnEncFail: false,
						});
					}
					localStorage.setItem(localStorageKeyForEncryptino, JSON.stringify({
						time: Date.now(),
						message: 'Your account was detected using a tempered/unsupported application. This is a violation and will be reviewed. Further action may be taken',
					}));
					window.location.reload();
					throw new Error('Invalid login attempt');
				}
				if (error?.response?.status === 499) {
					isLogout = 105;
					localStorage.setItem(localStorageKeyForVmDetection, JSON.stringify(Date.now()));
					window.location.reload();
					throw new Error('Tempering detected');
				}
				if (!error.code) {
					throw new Error('Please check your internet connection.');
				}
				if (error.code === 'ECONNABORTED') {
					throw new Error('Please check your internet connection.');
				}
			}
			if (error.message.indexOf('status code 401') !== -1) {
				isLogout = 106;
			} else if (error.message.indexOf('Network Error') !== -1) {
				throw new Error('Please check your internet connection.');
			}
			throw new Error(error.message);
		} finally {
			if (isLogout && !requestConfig?.allowWithoutLogin) {
				// need to change soon. Because we need to fix this issue soon that's why we did it.
				// message.error('Session Expired');
				// await (new Promise((res, rej) => {
				// 	setTimeout(res, 1000);
				// }));
				localStorage.setItem('forceSessionExpired', `${Date.now()}:1${isLogout}`);
				this.emit('logout-user', {
					reload: true,
				});
				//! In session expire i do not trust this code.
				// await (new Promise((res) => {
				// 	setTimeout(res, 3000);
				// }));
			}
		}
	};

	loginUser = async (
		email: string, password: string, quizCode: string, quizName: string,
	): Promise<unknown> => {
		try {
			const platform = currentPlatform();
			const response = await this.mainApiRequestSend({
				method: 'POST',
				url: '/login/?isQuiz=1',
				payload: {
					email,
					password,
					quizCode,
					quizName,
					platform,
				},
			}, {
				allowWithoutLogin: true,
				retryOnEncFail: true,
				encV2: true,
				getRowAxiosError: true,
			});
			if (response.status === 440) {
				this._appConfigChange();
				throw new Error('Oh, something went wrong please try awaing');
			}

			if (response.status === 205) {
				if (isElectronApp) {
					setTimeout(() => {
						this.emit('logout-user', { reload: true });
					}, 2000);
					throw new Error('Quiz has been updated. Reloading ..');
				} else {
					throw new Error('Test has been updated, Please reload and try again.');
				}
			}
			const data = response.data as unknown;
			return data;
		} catch (error: any) {
			if (axios.isAxiosError(error)) {
				const data = error?.response?.data;
				if (data?.level === BadActivitiesTypeString.CHEATING) {
					return {
						error: data?.error,
					};
				}
			}
			logger.error(error?.response);
			throw new Error(error?.response?.data?.error ?? error?.message ?? error ?? 'Login Error: Unknown Error');
		}
	};

	registerUser = async (
		name: string, email: string, password: string, quizCode: string,
		mobile: string, quizName: string, quizCreatedBy: string, enrollmentId?: string,
	): Promise<unknown> => {
		try {
			const response = await this.mainApiRequestSend({
				method: 'POST',
				url: '/addstudent/guest/',
				payload: {
					stname: name,
					stemail: email,
					stpassword: password,
					stcontactno: mobile,
					quizCode,
					quizName,
					userCreatorId: quizCreatedBy,
					enrollmentId,
					role: '1',
				},
			}, {
				encV2: true,
				allowWithoutLogin: true,
			});

			if (response.data && response.data.error) {
				logger.error(response.data.error);
				throw new Error(response.data.error);
			}

			const data = response.data as unknown;

			return data;
		} catch (e: any) {
			logger.error(e.message);
			throw new Error(e.message || 'Register Error: Unknown Error');
		}
	};

	getSessionData = async (): Promise<void> => {
		try {
			const response = await this.mainApiRequestSend({
				method: 'GET',
				url: '/quiz-api/session/data',
			}, {
				allowWithoutLogin: true,
			});
			const data = response.data as SessionDataResponse;
			let { profilePic } = data.session;
			if (profilePic.indexOf('digitaloceanspaces') === -1) {
				profilePic = '';
			}
			const {
				userId, ssnid, role, email, roleId, displayname, tryTest, enrollmentId,
			} = data.session;

			const {
				isClassAllowed,
				allowInteractiveMode,
				canAddTestCase,
				addQuestion,
				addCourse,
				addTutorial,
				addQuiz,
				addBatch,
				addUser,
				addAdminProject,
				projectSpace,
				testingSpace,
				learningSpace,
				projectLanguagesAllowed,
				languagesAllowed,
				quizId,
				authTokenForStream,
			} = data.session;

			if (this.quizId !== quizId) {
				throw new Error('Quiz id dees not match with session.');
			}

			const sessionData: SessionData = {
				userId,
				sessionId: ssnid,
				email,
				role: parseInt(role, 10),
				roleId,
				displayName: displayname,
				enrollmentId,
				profilePic,
				tryTest: tryTest || false,

				stream: {
					authToken: authTokenForStream,
				},

				acl: {
					isClassAllowed,
					allowInteractiveMode,
					canAddTestCase,
					addQuestion,
					addCourse,
					addTutorial,
					addQuiz,
					addBatch,
					addUser,
					addAdminProject,
					projectSpace,
					testingSpace,
					learningSpace,

					projectLanguagesAllowed,
					languagesAllowed,
				},
			};

			this.emit('auth', sessionData);
		} catch (e: any) {
			logger.error(e.response);
			throw new Error(e?.response?.data?.error || e?.message || 'Error while getting session data.');
		}
	};

	getQuizData = async (quizId: string): Promise<void> => {
		// const response = await this._mainAPI.get(`/test/${quizId}?json=1`);
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/test/${quizId}?json=1`,
		});

		if (response.data?.error && response.data?.redirectUrl) {
			// console.error(response.data.error);
			// ip not allowed case
			// throw new Error(response.data.error || 'Ip not allowed');
			window.location.href = response.data.redirectUrl;
			return;
		}

		const qData = response?.data.quiz as QuizDataResponse;

		this.quizId = qData._id;

		const isRecordingUploadBlockingEnabled = qData.isRecordingUploadBlockingEnabled === 'true'
			|| qData.isRecordingUploadBlockingEnabled === true;
		const isLiveStreamEnabled = (qData.isLiveStreamEnabled === 'true' || qData.isLiveStreamEnabled === true)
			&& (qData.isWebCamAllowed === 'true' || qData.isWebCamAllowed === true);
		const quizData: QuizData = {
			quizId: qData._id,
			title: qData.title,
			instructions: qData.instructions,
			startTime: qData.startTime,
			questions: qData.questions,
			quizTime: qData.quizTime,
			languagesAllowed: qData.languagesAllowed || [],
			quizParams: qData.quizParams || [],
			quizUserDetails: qData.quizUserDetails,
			attemptInSequence: qData.attemptInSequence === 'true' || qData.attemptInSequence === true,
			copyPasteAllowed: qData.copyPasteAllowed === 'true' || qData.copyPasteAllowed === true,
			isPrivate: qData.isPrivate === 'true' || qData.isPrivate === true,
			isSignUpAllowed: qData.isSignUpAllowed === 'true' || qData.isSignUpAllowed === true,
			isWebCamAllowed: (qData.isWebCamAllowed === 'true' || qData.isWebCamAllowed === true),
			randomizeQuestion: qData.randomizeQuestion === 'true' || qData.randomizeQuestion === true,
			revisitAllowed: qData.revisitAllowed === 'true' || qData.revisitAllowed === true,
			tabSwitchAllowed: qData.tabSwitchAllowed === 'true' || qData.tabSwitchAllowed === true,
			createdBy: qData.userCreatorId,
			updatedAt: qData.updatedAt,
			logo: qData.logo,
			isFullScreen: qData.isFullScreen === 'true' || qData.isFullScreen === true,
			isAppOnly: qData.isAppOnly === 'true' || qData.isAppOnly === true,
			isRecordingEnabled: qData.isRecordingEnabled === 'true' || qData.isRecordingEnabled === true,
			isRecordingUploadBlockingEnabled,
			maxWaitForRecordingToUpload: isRecordingUploadBlockingEnabled
				? qData.maxWaitForRecordingToUpload ?? 60 * 1000
				: 0,
			isLiveStreamEnabled: !!isLiveStreamEnabled,
			onlySuspiciousRoom: (qData.onlySuspiciousRoom === 'true' || qData.onlySuspiciousRoom === true)
				&& (isLiveStreamEnabled === true),
			allowClose: qData.allowClose ? (qData.allowClose === 'true' || qData.allowClose === true) : false,
			invalidIp: qData.invalidIp,
			endTime: qData.endTime,
			isSubmitScreenShotEnabled: false,
			toShuffleMCQOptions: (qData.toShuffleMCQOptions === 'true' || qData.toShuffleMCQOptions === true),
			isWatermarkEnabled: (qData.isWatermarkEnabled === 'true' || qData.isWatermarkEnabled === true),
			isCustomWatermarkEnabled: (qData.isCustomWatermarkEnabled === 'true' || qData.isCustomWatermarkEnabled === true),
			customWatermark: qData.customWatermark,
		};

		this.emit('quiz-data-received', quizData);
		this.appConfigChage();
	};

	startQuiz = async (quizId: string, quizUserDetails?: any): Promise<void> => {
		if (this.connected && navigator.onLine) {
			const startQuizPayload = {
				quizUserDetails,
			};
			const startQuizContext = {
				quizId,
				userId: this._sessionData?.userId,
				hasQuizUserDetails: !!quizUserDetails,
				quizUserDetailsKeys: quizUserDetails && typeof quizUserDetails === 'object'
					? Object.keys(quizUserDetails)
					: [],
				payloadSize: getJsonPayloadSize(startQuizPayload),
			};
			trackOpenReplayEvent('startQuiz.request', startQuizContext);
			// const response = await this._mainAPI.post(`/test/startQuiz/${quizId}`, {
			// 	quizUserDetails,
			// });
			try {
				const response = await this.mainApiRequestSend({
					method: 'POST',
					url: `/test/startQuiz/${quizId}`,
					payload: startQuizPayload,
				});
				trackOpenReplayEvent('startQuiz.response', {
					...startQuizContext,
					status: response?.status,
					hasRemainingTime: response?.data?.remainingTime !== undefined,
				});
				console.log('Quiz Has been started no error thrown here');
				this.emit('quiz-started', { remainingTime: response?.data?.remainingTime });
			} catch (error: any) {
				trackOpenReplayIssue('startQuiz.error', {
					...startQuizContext,
					message: error?.message,
					code: error?.code,
					status: error?.response?.status,
				});
				throw error;
			}
		} else {
			window.location.reload();
		}
	};

	getQuizDashboardData = async (quizId: string, isUpdate?: number): Promise<void> => {
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/test/dashboard/${quizId}`,
			params: {
				update: isUpdate || undefined,
			},
		});

		// const response = await this._mainAPI.get(`/test/dashboard/${quizId}`, {
		// 	params: {
		// 		update: isUpdate || undefined,
		// 	},
		// });

		const data = response?.data as QuizDashboardDataResponse;

		const dashboardData: QuizDashboardData = {
			segments: [],
			questionIds: data.userQuizSubmittedSegment.questionId,
		};

		const { revisitAllowed } = data.quiz;

		let startIndex = 0;

		dashboardData.segments = data.quiz.quizSegments.map((segment, index) => {
			const isSubmitted = data?.userQuizSubmittedSegment?.quizSubmittedSegments?.findIndex(
				(segIndex: string) => +segIndex === index,
			);

			const segmentData: QuizDashboardSegmentData = {
				_id: segment._id,
				title: segment.title,
				count: segment.count,
				isDisabled: isSubmitted < 0 && !revisitAllowed,
				isSubmitted: isSubmitted >= 0 && !revisitAllowed,
				questions: data.quiz.poolQuestion || data.quiz.randomizeQuestion
					? data.userQuizSubmittedSegment.questionId
						.slice(startIndex)
						.slice(0,
							data.quiz.poolQuestion && segment.pollNumber ? segment.pollNumber : segment.count)
						.map((qId) => data.quiz.quizContent.find((el) => el.id === qId))
						.filter((content) => !!(content && content.question))
						.map((content) => ({
							_id: content?.question._id,
							title: content?.question.title,
							type: content?.question.type ? +content.question.type : NaN,
							status: data.userQuizSubmittedSegment
								.submittedQuestions.findIndex((qId) => qId === content?.question._id) < 0
								? 0 : 1,
						}))
					: data.quiz.quizContent
						.slice(startIndex)
						.slice(0, segment.count)
						.filter((content) => !!(content && content.question))
						.map((content) => ({
							_id: content.question._id,
							title: content.question.title,
							type: +content.question.type,
							status: data.userQuizSubmittedSegment
								.submittedQuestions.findIndex((qId) => qId === content?.question._id) < 0
								? 0 : 1,
						})),
			};

			startIndex += data.quiz.poolQuestion ? segment.pollNumber : segment.count;

			return segmentData;
		});

		let lastSubmittedIndex = -1;
		dashboardData.segments.forEach((segment, index) => {
			if (segment.isSubmitted) {
				lastSubmittedIndex = index;
			}
		});

		if (lastSubmittedIndex >= 0) {
			const segment = dashboardData.segments[lastSubmittedIndex + 1];
			if (segment) {
				segment.isDisabled = false;
			}
		} else if (dashboardData.segments[0]) {
			dashboardData.segments[0].isDisabled = false;
		}

		this.emit('quiz-dashboard-data-received', {
			dashboardData,
			quiz: {
				...data.quiz,
				totalQuestions: data.quiz.poolQuestion
					? data.userQuizSubmittedSegment.questionId.length : data.quiz.questionId.length,
			},
			submittedQuestions: data.userQuizSubmittedSegment.submittedQuestions.filter((qId) => {
				let questionIds = data.quiz.questionId;
				if (data.userQuizSubmittedSegment.questionId?.length) {
					questionIds = data.userQuizSubmittedSegment.questionId;
				}

				return questionIds.includes(qId as string);
			}),
			hasQuizStarted: data.userQuizSubmittedSegment.hasQuizStarted,
		});
	};

	getAttemptQuestionData = async (quizId: string, questionId: string): Promise<void> => {
		// const response =
		// await this._mainAPI.get(`/attempt/attemptquestion/quiz/${quizId}/${questionId}`);
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/attempt/attemptquestion/quiz/${quizId}/${questionId}`,
		});

		const data = response.data as AttemptQuestionDataResponse;

		if (data.question.variationId) {
			this._variationIdByQuestion.set(data.question._id, data.question.variationId);
		}

		const attemptQuestionData: AttemptQuestionData = {
			_id: data.question._id,
			variationId: data.question.variationId,
			title: data.question.title,
			text: data.question.text,
			type: data.question.type,
			lastUsed: data.question.lastUsed,
			score: `${data.question.score}`,
			files: data.question.files,
		};

		if (+attemptQuestionData.type === QuestionType.MCQ && data.question.questionTypeMCQ) {
			attemptQuestionData.questionTypeMCQ = {
				correctAnswers: data.question.questionTypeMCQ.correctAnswers,
				options: data.question.questionTypeMCQ.options,
				showExplanation: data.question.questionTypeMCQ.showExplanation,
				lastSubmitted: data.latestAttempt?.userInputMCQ,
			};
		} else if (+attemptQuestionData.type === QuestionType.MQ && data.question.questionTypeCoding) {
			const {
				testCase, executionType, multipleTestCases,
			} = data.question.questionTypeCoding;

			attemptQuestionData.questionTypeMQ = {
				executionType,
				multipleTestCases,
				testCases: testCase.map((el) => ({
					_id: el._id,
					attemptInMultiLine: el.attemptInMultiLine,
					sampleTest: !!el.sampleTest,
					expectedOutput: el.codeprogexpectedoutput,
					input: el.codeproginputparams,
					score: el.scoreip,
				})),
				lastSubmitted: data.latestAttempt?.userOutputCoding,
			};
		} else if (
			+attemptQuestionData.type === QuestionType.Coding && data.question.questionTypeCoding
		) {
			const {
				testCase, executionType, multipleTestCases, codeproglang,
			} = data.question.questionTypeCoding;

			attemptQuestionData.questionTypeCoding = {
				showHead: data.question.showHead,
				showTail: data.question.showTail,
				showCustomInput: data.question.showCustomInput,
				executionType,
				multipleTestCases,
				testCases: testCase.map((el) => ({
					_id: el._id,
					attemptInMultiLine: el.attemptInMultiLine,
					sampleTest: !!el.sampleTest,
					expectedOutput: el.codeprogexpectedoutput,
					input: el.codeproginputparams,
					score: el.scoreip,
					status: TestCaseStatus.NotExecuted,
					userOutput: '',
					onCloud: el.onCloud ?? false,
				})),
				codeLanguages: codeproglang.map((el) => ({
					_id: el._id,
					defaultCompileResult: el.defaultCompileResult,
					defaultTrimmedCode: el.defaultTrimmedCode,
					language: el.language,
					codeComponents: el.codeComponents,
				})),
				lastSubmitted: data.latestAttempt ? {
					language: data.latestAttempt.userLanguage,
					code: data.latestAttempt.userProgram?.replace(/^"|"$/g, '').replace(/\\n/g, '\n').replace(/\\"/g, '"'),
				} : undefined,
			};
		} else if (
			+attemptQuestionData.type === QuestionType.Web && data.question.questionTypeWeb
		) {
			attemptQuestionData.questionTypeWeb = {
				...data.question.questionTypeWeb,
				testCases: data.question.questionTypeWeb.testCase
					? data.question.questionTypeWeb.testCase.map((el) => ({
						_id: el._id,
						sampleTest: el.sampleTest,
						description: el.description,
						evaluator: el.evaluator,
						score: el.scoreip,
						status: TestCaseStatus.NotExecuted,
					})) : [],
				lastSubmitted: data.latestAttempt ? {
					html: data.latestAttempt.html,
					css: data.latestAttempt.css,
					js: data.latestAttempt.js,
				} : undefined,
			};
		} else if (+attemptQuestionData.type === QuestionType.Subjective) {
			attemptQuestionData.questionTypeSubjective = {
				isFileUpload: data?.question?.questionTypeSubjective?.isFileUpload || false,
				lastSubmitted: data.latestAttempt?.userInputSubjective,
			};
		}

		this.emit('attempt-question-data-received', attemptQuestionData);
	};

	submitQuestion = async (
		quizId: string, questionId: string, requestPayload: any,
	): Promise<void> => {
		const formData = new FormData();
		const variationId = this._variationIdByQuestion.get(questionId);
		if (variationId && requestPayload.variationId === undefined) {
			formData.append('variationId', variationId);
		}
		Object.entries(requestPayload).forEach((entry) => {
			if (entry[1] instanceof Array) {
				entry[1].forEach((val: any) => formData.append(`${entry[0]}[]`, val));
			} else {
				formData.append(entry[0], entry[1] as any);
			}
		});

		// const response = await this._mainAPI.post(
		// 	`/attempt/attemptquestion/quiz/${quizId}/${questionId}`,
		// 	formData,
		// 	{
		// 		headers: {
		// 			'content-type': 'multipart/form-data',
		// 		},
		// 	},
		// );

		const response = await this.mainApiRequestSend({
			method: 'POST',
			url: `/attempt/attemptquestion/quiz/${quizId}/${questionId}`,
			payload: formData,
			headers: {
				'content-type': 'multipart/form-data',
			},
		});

		const data = response?.data as any;
		if (data.error) {
			throw new Error(data.error);
		}

		this.emit('question-submitted', questionId);
	};

	submitQuizSegment = async (
		quizId: string, questionId: string, segmentIndex: number, imageURL: string | null,
		otp?: string,
	): Promise<void> => {
		// const response = await this._mainAPI.post(
		// 	`/attempt/submitsegment/quiz/${quizId}/${questionId}/${segmentIndex}`,
		// );
		const payload: { [key: string]: string } = {};
		if (imageURL) {
			payload.imageURL = imageURL;
		}
		if (otp) {
			payload.otp = otp;
		}
		const response = await this.mainApiRequestSend({
			method: 'POST',
			url: `/attempt/submitsegment/quiz/${quizId}/${questionId}/${segmentIndex}`,
			payload,
		});

		const data = response.data as any;
		if (data.error) {
			throw new Error(data.error);
		}

		this.emit('quiz-segment-submitted', segmentIndex);
	};

	submitQuiz = async (
		quizId: string, isAutoSubmit?: boolean,
		isForceSubmit?: boolean, imageURL?: string, otp?: string,
	): Promise<string | void> => {
		// const response = await this._mainAPI.get(`/attempt/submitQuiz/${quizId}`, {
		// 	params: {
		// 		isAutoSubmit: isAutoSubmit ? 1 : 0,
		// 	},
		// });
		const params: { [key: string]: any } = {
			isAutoSubmit: isAutoSubmit ? 1 : 0,
			isForceSubmit: isForceSubmit ? 1 : 0,
		};
		if (imageURL) {
			params.imageURL = imageURL;
		}
		if (otp) {
			params.otp = otp;
		}
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/attempt/submitQuiz/${quizId}`,
			params,
		});

		const data = response.data as any;
		if (data.error) {
			throw new Error(data.error);
		}

		this.emit('quiz-submitted');

		return data.redirectLink;
	};

	getRemainingTime = async (quizId: string): Promise<void> => {
		let payload: any = {
			quizId,
		};
		if ('api' in window) {
			payload = await (window.api as any)._invoke('_encrypt-payload', payload);
		}
		// this._socket.emit('quiz_remaining_time', payload);
		// const response = await this._mainAPI.get(`/test/remainingTime/${quizId}`);
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/test/remainingTime/${quizId}`,
		});

		const data = response.data as QuizRemainingTimeResponse;
		if (data.error) {
			throw new Error(data.error);
		}

		const time: number = +data.remainingTime + +data.extraTime;

		if (data.quizUpdatedAt) {
			this.emit('quiz-updated', data.quizUpdatedAt);
		}

		this.emit('quiz-remaining-time-updated', time);
		this.emit('quiz-suspicious-updated', data);
	};

	uploadEvent = async (data: any): Promise<void> => {
		this._socket.emit('recording_upload_event', data);
	};

	compileCode = async (request: CodeCompilationRequest): Promise<void> => {
		const { _socket } = this;
		const compileRequest: CodeCompilationRequest = {
			...request,
			variationId: request.variationId ?? this._variationIdByQuestion.get(request.questionId),
		};

		return new Promise((resolve, reject) => {
			let resolved = false;

			if (!navigator.onLine) {
				resolved = true;

				logger.error('No internet for compile');
				reject(new Error('Please check your internet connection.'));
			}

			const timer = setTimeout(() => {
				resolved = true;

				logger.error('timeout waiting for compile');
				reject(new Error('timed out'));
			}, 10000);

			_socket.emit('compile', compileRequest, (error) => {
				if (resolved) return;

				resolved = true;
				clearTimeout(timer);

				if (error) {
					logger.error(`compile error: ${error}`);
					reject(new Error('failed to compile'));
				} else {
					resolve();

					if (compileRequest.questionSubmission) {
						this.emit('question-submitted', compileRequest.questionId);
					}
				}
			});
		});
	};

	sendCheatingDetails = async (
		cheatingType: number,
		tabSwitchType?: number,
		timeStamp?: number,
	): Promise<void> => {
		this._sendNotification({ cheatingType, data: { tabSwitchType, timeStamp } });
	};

	sendAiProctoringResult = async (type: number, data: Record<string, unknown>): Promise<void> => {
		this._sendNotification({ cheatingType: type, data });
	};

	sendImageDataSync = async (
		userId: string, quizId: string, sessionId: string, imageData: unknown,
	): Promise<string> => {
		try {
			const result = await this._staticStorageAPI.post('/uploadSync', {
				userId,
				quizId,
				sId: sessionId,
				data: imageData,
			});
			if (result.data.error) {
				throw new Error(result.data.error);
			}
			console.log('Result of upload', result.data);
			return (result.data?.data?.url ?? '');
		} catch (error) {
			console.log(error);
			throw error;
		}
	};

	presignedUrlForSubjectiveQuestion = async (
		data: {
			questionId: string,
			userId: string,
			quizId: string
		},
		uploadConfig: Array<{ name: string, contentType: string, }>,
	): Promise<Array<{ url: string, fields: Record<string, string>, location: string }>> => {
		const response = await this._staticStorageAPI.post(
			'/getPresignedUrlForSubjectiveFileUpload',
			{
				questionId: data.questionId,
				files: uploadConfig,
				userId: data.userId,
				quizId: data.quizId,
			},
		);
		return response.data;
	};

	sendImageData = async (
		userId: string, quizId: string, sessionId: string, imageData: unknown,
	): Promise<void> => {
		try {
			await this._staticStorageAPI.post('/upload', {
				userId,
				quizId,
				sId: sessionId,
				data: imageData,
			});
		} catch (e: any) {
			logger.error('error while sending invigilator data ==> ', e);
		}
	};

	getServerTime = async (): Promise<void> => {
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: '/api/getServerTime',
		});

		if (response.error) {
			throw new Error(response.error);
		}
		this.emit('server-time', response.data.Date);
	};

	downloadApp = async (platform?: string): Promise<void> => {
		const url = `${this._quizServerUrl}/app/download${(platform) ? `/${platform}` : ''}`;
		window.open(url, '_blank');
	};

	forgotPassword = async (data: forgotPasswordPayload): Promise<void> => {
		const response = await this.mainApiRequestSend({
			method: 'POST',
			url: '/api/forgot',
		});
		if (response.error) {
			throw new Error(response.error);
		}
		return response?.data;
	};

	redirectToQuizClient = (path: string): void => {
		window.location.href = `${this._quizServerUrl}${path}`;
	};

	validateToken = async (token: string): Promise<boolean> => {
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: `/test/validateToken/${token}`,
		});
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response?.data?.data;
	};

	getLiveStreamRoom = async (): Promise<Array<string>> => {
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: '/quiz-api/liveStreamRoom',
		});
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response?.data?.rooms;
	};

	getJitsiCredentials = async (): Promise<JitsiCredentials> => {
		const response = await this.mainApiRequestSend({
			method: 'GET',
			url: '/quiz-api/jitsiCredentials',
		});
		if (response.data.error) {
			throw new Error(response.data.error);
		}
		return response?.data?.credentials;
	};

	updateJitsiCredentials = async (data: NonNullable<JitsiCredentials>) => {
		this._socket.emit('update_jitsi_credentials', data);
	};

	sendSubmissionLog = async (data: SubmissionPayload) => {
		this._socket.emit('send_button_submission_logs', data);
	};

	validateEmail = async (
		data: { email: string, token: string, quizName: string },
	): Promise<void | { error?: string }> => {
		try {
			const url = `/test/validateEmail/${data.token}`;
			const response = await this.mainApiRequestSend({
				method: 'POST',
				url,
				payload: {
					email: data.email,
				},
			}, {
				allowWithoutLogin: true,
				retryOnEncFail: true,
				encV2: true,
				getRowAxiosError: true,
			});
			if (response.data.error) {
				throw new Error(response.data.error);
			}
			return undefined;
		} catch (error: any) {
			if (axios.isAxiosError(error)) {
				const res = error?.response?.data;
				if (
					res?.level === BadActivitiesTypeString.CHEATING
					&& 'error' in res && typeof res.error === 'string'
				) {
					return {
						error: res.error,
					};
				}
			}
			logger.error(error?.response);
			throw new Error(
				error?.response?.data?.error
				?? error?.message ?? error
				?? 'Login Error: Unknown Error',
			);
		}
	};

	userRemovedFromRoom = (data) => {
		this._socket.emit('removed_user_from_room', data);
	};

	appConfigChage = async () => {
		try {
			if (isElectronApp && 'api' in window) {
				(window.api as any).updateAppConfig();
			}
		} catch (error) {
			console.error(error);
		}
	};

	remoteAction = async (data) => {
		try {
			if (isElectronApp && 'api' in window) {
				(window.api as any).remoteAction(data);
			}
		} catch (error) {
			console.error(error);
		}
	};

	sendRemoteActionResponse = async (data) => {
		this._socket.emit('heart_beat_complete', data);
	};

	sendTemperingDetected = async (quizId: string): Promise<void> => {
		try {
			await this._mainAPI({
				url: '/app/integrity-fail',
				method: 'PUT',
				data: {
					quizId,
				},
			});
		} catch (error) {
			console.error(error);
		}
	};

	validateKey = async (cipherText: string): Promise<boolean> => {
		try {
			const key = CryptoJS.enc.Hex.parse('1f8e6d37a4b8dcd06a3cbf88c7eec7f2');
			const iv = CryptoJS.enc.Hex.parse('fbb9f284c3d9da3ac8b52e6f5bc7c248');

			console.log('To Desipher', cipherText);
			const decrypted = CryptoJS.AES.decrypt(
				{
					ciphertext: CryptoJS.enc.Hex.parse(cipherText),
				},
				key, {
					iv,
					mode: CryptoJS.mode.CTR,
					padding: CryptoJS.pad.NoPadding,
				},
			);

			const dec = decrypted.toString(CryptoJS.enc.Utf8);
			return dec === 'This-Is-Temp-Key-Random-Random-TWO-Random';
		} catch (error) {
			console.error('Decryption error:', error);
			return false;
		}
	};

	checkForVM = async (
		payload: Record<string, unknown>, headers: Record<string, unknown>,
	): Promise<void> => {
		await this._sendNotification({ ...payload }, headers, {
			async handleSendForNonNotificationsServer(client) {
				await client.mainApiRequestSend({
					api: '_quizAdminAPI',
					url: '/quiz-api/vm-detection',
					method: 'POST',
					payload,
					headers,
				}, { allowWithoutLogin: true, skipEnc: true });
			},
		});
	};

	sendContentProtection = async (
		payload: Record<string, unknown>,
		headers: Record<string, unknown>,
	): Promise<unknown> => {
		try {
			const response = await this.mainApiRequestSend({
				api: '_quizAdminAPI',
				url: '/quiz-api/content-protection',
				method: 'POST',
				payload,
				headers,
			}, { allowWithoutLogin: true, skipEnc: true });
			return response;
		} catch (error) {
			console.error(error);
		}
		return null;
	};
}
